import { Util } from "../../../../util/util";
import { BaseTriggerItemsQualifier } from "./base-trigger-items";
export class GroupItemBaseTriggerItemsQualifier extends BaseTriggerItemsQualifier {
    _getTriggerItems(coupon, cart) {
        const groups = new Map();
        for (const item of coupon.items) {
            const arr = groups.get(item.groupIndex);
            if (arr)
                arr.push(item);
            else
                groups.set(item.groupIndex, [item]);
        }
        const validItems = [];
        for (const [groupIndex, couponGroupItems] of groups) {
            const validGroupItems = this._qualifyCouponItemsGroupQTY(groupIndex, coupon, cart, couponGroupItems);
            if (!validGroupItems?.length)
                return [];
            validItems.push(...validGroupItems);
        }
        return validItems;
    }
    _qualifyCouponItemsGroup(group, coupon, cart, cItem) {
        const qItem = this._findQualifyingItem(group, coupon, cart, cItem);
        return qItem && qItem.index > -1 ? qItem : null;
    }
    _findQualifyingItem(group, coupon, cart, cItem) {
        const cItemId = cItem.itemId;
        const cSizeId = cItem.sizeId;
        const cModifierCount = cItem.modifierCount;
        const cAddOnPrice = cItem.addOnPrice || 0;
        let exactMatchFound = false;
        let items = this._cartItems.filter(x => this._isItemEligible(x, coupon, cart, cItemId, cSizeId) &&
            this._modifierEquals(x, cModifierCount));
        if (items.length === 0) {
            items = this._cartItems.filter(x => this._isItemEligible(x, coupon, cart, cItemId, cSizeId) &&
                this._modifierGreater(x, cModifierCount));
        }
        else {
            exactMatchFound = true;
        }
        // For half & half items, every half must independently meet the coupon's
        // qualifying-items criteria before the item can be treated as a match. A
        // single qualifying half no longer grants the discount to its siblings.
        const cartItem = items.find(x => x.halfCount <= 1 || this._allHalvesQualify(group, coupon, cart, x));
        const matchingItemIndex = cartItem ? this._cartItems.indexOf(cartItem) : -1;
        if (matchingItemIndex === -1 && cModifierCount > 0 && ((!coupon.exactMatch || !coupon.autoApplyExactMatch))) {
            const newModCount = cModifierCount - 1;
            const cItemOverride = coupon.items.find(x => x.groupIndex === group && x.itemId == cItemId && x.modifierCount === newModCount);
            const cItemClone = structuredClone(cItem);
            cItemClone.modifierCount = newModCount;
            cItemClone.addOnPrice = cItemOverride?.addOnPrice ?? cAddOnPrice;
            return this._findQualifyingItem(group, coupon, cart, cItemClone);
        }
        if (!cartItem || items.length === 0 || (coupon.exactMatch && !exactMatchFound)) {
            return null;
        }
        if (cartItem.halfCount > 1) {
            cart.markAllHalves(this._cartItems, cartItem.itemKey);
        }
        else {
            cartItem.discountedMarkedQuantity += 1;
        }
        return {
            index: matchingItemIndex,
            modifierCount: cModifierCount,
            addOnPrice: cAddOnPrice,
            groupIndex: group
        };
    }
    /**
     * A half & half item qualifies only when every half sharing its itemKey is on the coupon's
     * qualifying-items list for this group and is otherwise eligible. Enforces ADO #82541: all
     * halves must meet criteria rather than inheriting the discount from the first qualifying half.
     *
     * Matching is on item and size alone. A half carrying fewer priced modifiers than its row's
     * Toppings Covered still qualifies — it is priced at the coverage it actually carries, the same
     * way the decrement fallback in _findQualifyingItem prices an under-topped whole item. Only an
     * exact-match coupon holds the halves to the configured count.
     */
    _allHalvesQualify(group, coupon, cart, cartItem) {
        const halves = this._cartItems.filter(x => x.itemKey === cartItem.itemKey);
        // All configured halves must be present in the cart.
        if (halves.length !== cartItem.halfCount)
            return false;
        const groupItems = coupon.items.filter(x => x.groupIndex === group);
        return halves.every(half => groupItems.some(gItem => this._isItemEligible(half, coupon, cart, gItem.itemId, gItem.sizeId) &&
            (!coupon.exactMatch || this._modifierEquals(half, gItem.modifierCount))));
    }
    _isItemEligible(x, coupon, cart, itemId, sizeId) {
        return x.id === itemId &&
            x.sizeId === sizeId &&
            !x.isRemoved &&
            ((x.discountedQuantity + x.discountedMarkedQuantity < x.quantity) ||
                (coupon.allowOtherCoupons && x.discountedMarkedQuantity < x.quantity)) &&
            (!coupon.allowOtherCoupons || (coupon.allowOtherCoupons && !Util.CheckSameCouponAppliedQtyTimes(cart, x, coupon.couponId))) &&
            (!coupon.excludeLoyaltyUsage || (coupon.excludeLoyaltyUsage && !x.isRedeemed));
    }
    _modifierEquals(x, modifierCount) {
        return Util.GetItemPricedModifierCount(x, this._data.preModifiers) === modifierCount;
    }
    _modifierGreater(x, modifierCount) {
        return Util.GetItemPricedModifierCount(x, this._data.preModifiers) > modifierCount;
    }
}
//# sourceMappingURL=group-item-base-trigger-items.js.map